import { useEffect, useRef, useState } from 'react';
import { useAppStore, type View } from './store/useAppStore.js';
import { supabase } from './lib/supabase.js';
import { readSession } from './lib/session.js';
import { LoginView } from './views/LoginView.js';
import { LoginOnlyView } from './views/LoginOnlyView.js';
import { ForgotPasswordView } from './views/ForgotPasswordView.js';
import { ResetPasswordView } from './views/ResetPasswordView.js';
import { RatingView } from './views/RatingView.js';
import { LobbyView } from './views/LobbyView.js';
import { IncomingView } from './views/IncomingView.js';
import { CallView } from './views/CallView.js';
import { LoopbackView } from './views/LoopbackView.js';
import { RegisterLearnerView } from './views/RegisterLearnerView.js';
import { RegisterGuideView } from './views/RegisterGuideView.js';
import { viewForPath, navigateToView } from './registration/routing.js';
import { LobbyProvider } from './lobby/LobbyContext.js';
import { DebugPanel } from './debug/DebugPanel.js';
import { FinancialDrawer } from './views/FinancialDrawer.js';
import { ProfileView } from './views/ProfileView.js';
import { useTheme } from './lib/theme.js';
import { SunIcon, MoonIcon, GearIcon, ClockIcon } from './icons.js';
import { TopBar } from './TopBar.js';
import { OverflowMenu } from './OverflowMenu.js';
import { useAdminStatus } from './admin/useAdminStatus.js';
import { useIdlePrompt } from './hooks/useIdlePrompt.js';
import { useLobbyHeartbeat } from './hooks/useLobbyHeartbeat.js';
import { useScrollToTop } from './hooks/useScrollToTop.js';
import { IdleModal } from './views/IdleModal.js';
import { SupportView } from './views/InfoViews.js';
import { GuideStageRouter } from './views/guide/GuideStageRouter.js';

const INFO_VIEWS: View[] = ['support'];

// Captured at module load BEFORE supabase-js's URL-detection clears the hash.
// True only for the tab whose URL actually delivered the email-confirmation
// fragment; cross-tab storage syncs in other tabs leave this false, so we can
// distinguish "this is the confirming tab" from "another tab in the same
// origin just got a fresh session via cross-tab broadcast".
const IS_EMAIL_CONFIRM_TAB =
  typeof window !== 'undefined' &&
  window.location.hash.includes('access_token=') &&
  /[?&]?type=(signup|magiclink|invite|email_change)/.test(window.location.hash);

// The iOS app hands a learner to the web to buy minutes (Apple keeps purchases
// off-app) through a magic link whose redirect lands on /?buy=1. Captured at
// module load like the fragment above; consumed by consumeBuyParam() once the
// session and profile are known, because only a signed-in learner gets sent
// to the purchase flow.
const HAS_BUY_PARAM =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('buy') === '1';

/** Strip `?buy=1` from the address bar (keeping any other params and the
 *  hash) and, for a signed-in learner, set the store's buyIntent so the
 *  wallet panel takes over. Safe to call more than once. */
function consumeBuyParam(): void {
  if (!HAS_BUY_PARAM) return;
  const s = useAppStore.getState();
  if (s.userId && s.role === 'learner') s.setBuyIntent(true);
  const url = new URL(window.location.href);
  if (url.searchParams.has('buy')) {
    url.searchParams.delete('buy');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }
}

export function App() {
  const view = useAppStore((s) => s.view);
  const userId = useAppStore((s) => s.userId);
  const displayName = useAppStore((s) => s.displayName);
  const signOut = useAppStore((s) => s.signOut);
  const setView = useAppStore((s) => s.setView);
  const refreshProfile = useAppStore((s) => s.refreshProfile);
  const { theme, toggle: toggleTheme } = useTheme();

  // True on first paint when a session token is already in storage. A returning
  // signed-in user would otherwise see a flash of LoginView (the store's default
  // view) while refreshProfile() makes its network round-trip; we know
  // synchronously from storage that they're signed in, so show a neutral loader
  // until the bootstrap effect below routes them into the lobby (or clears it
  // back to LoginView if the stored session turns out to be stale).
  const [bootstrapping, setBootstrapping] = useState(() => readSession() !== null);

  useEffect(() => {
    let active = true;
    // Bootstrap on mount: check storage directly for a session rather than
    // going through supabase.auth.getSession() (which can wedge after a tab
    // visibility change). If a session is present, load profile + roster and
    // route to lobby.
    (async () => {
      const stored = readSession();
      if (!stored) {
        // No session: honour register-URL deep-links so unauthenticated users
        // can reach the registration wizard from a direct link.
        const routeView = viewForPath(window.location.pathname);
        if (routeView) setView(routeView);
        // Nobody signed in (an expired hand-off link, say): the param is
        // meaningless, drop it. A magic-link tab still gets its chance below
        // in the SIGNED_IN handler — HAS_BUY_PARAM was captured at load.
        consumeBuyParam();
        if (active) setBootstrapping(false);
        return;
      }
      try {
        await refreshProfile();
        if (!active) return;
        // Only route into the lobby if a profile actually loaded. A stored
        // session whose access token has expired and can't be refreshed yields
        // a null userId (refreshProfile resolves without throwing, and the
        // dead session is cleared from storage) — that user belongs on
        // LoginView, not a lobby with nobody signed in.
        const s = useAppStore.getState();
        // Honour an authed deep-link to /profile on reload; otherwise land in
        // the lobby. (viewForPath only resolves URL-mapped views — currently
        // the register wizards and /profile.)
        const deepView = viewForPath(window.location.pathname);
        if (s.userId && deepView === 'profile') setView('profile');
        else if (s.userId && s.view === 'login') setView('lobby');
        consumeBuyParam();
      } catch {
        // Profile load failed (e.g., stale session). Leave the user on
        // LoginView; they can re-enter credentials.
      } finally {
        if (active) setBootstrapping(false);
      }
    })();
    // supabase-js is only here for URL-fragment detection. SignIn / signOut
    // go through our direct-fetch code in useAppStore, so SIGNED_IN events
    // from our own code path never fire. The two cases supabase-js handles:
    //   - PASSWORD_RECOVERY: type=recovery hash from a reset-password email
    //   - SIGNED_IN with session: type=signup hash from an email-confirmation
    //     link; supabase-js writes the session to storage and we then need
    //     to load the profile + route the user into the lobby.
    //
    // Cross-tab caveat: supabase-js also fires SIGNED_IN in *every* tab of
    // the same origin when localStorage changes (cross-tab session sync).
    // If every tab auto-routed to the lobby, two tabs would try to join
    // LiveKit as the same identity and the SFU would kick one of them with
    // a DISCONNECT ~10 s later ("could not establish pc connection"). Gate
    // the navigation on IS_EMAIL_CONFIRM_TAB — only the tab whose URL
    // actually carried the verification fragment should claim the lobby.
    const { data: sub } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!active) return;
      if (event === 'PASSWORD_RECOVERY') {
        setView('reset');
      } else if (event === 'SIGNED_IN' && session && IS_EMAIL_CONFIRM_TAB) {
        try {
          await refreshProfile();
          if (!active) return;
          const v = useAppStore.getState().view;
          if (v === 'login') setView('lobby');
          consumeBuyParam();
        } catch { /* leave on current view */ }
      }
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [refreshProfile, setView]);

  const [financialDrawerOpen, setFinancialDrawerOpen] = useState(false);
  const adminStatus = useAdminStatus();
  const role = useAppStore((s) => s.role);
  const registrationStatus = useAppStore((s) => s.registrationStatus);
  const sessionCountdownSeconds = useAppStore((s) => s.sessionCountdownSeconds);
  const accountPending = registrationStatus === 'pending_review';
  const isLearner = role === 'learner';
  const inLobby = !!userId && (view === 'lobby' || view === 'incoming');
  const heartbeat = useLobbyHeartbeat({ enabled: inLobby });
  const idle = useIdlePrompt({
    enabled: inLobby,
    idleMs: 2 * 60 * 60 * 1000,
    promptMs: 2 * 60 * 1000,
    onTimeout: () => { void signOut(); },
    onDismissExtra: () => { heartbeat.pulse(); },
  });
  const isCall = view === 'call';
  const isLearnerLobby = view === 'lobby' && isLearner;
  // Views swap in place with no browser navigation, so the document keeps the
  // previous view's scroll offset. Snap back to the top on every change.
  useScrollToTop(view);

  // Footer info pages (Support). Remember the view the user came from so
  // "← Back" returns there rather than guessing lobby vs. login.
  const lastAppView = useRef<View>('lobby');
  const openInfo = (v: View) => {
    if (!INFO_VIEWS.includes(view)) lastAppView.current = view;
    setView(v);
  };

  // Restoring a returning user's session: show a neutral loader rather than the
  // logged-out chrome (header reads "Not connected" while nobody is signed in) or
  // a flash of LoginView. Cleared once the bootstrap effect resolves.
  if (bootstrapping) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-5 bg-canvas-light dark:bg-canvas-dark text-ink-900 dark:text-ink-100">
        <img src="/asl-go-logo.avif" alt="ASL Go" className="h-8 w-auto select-none" />
        <div
          role="status"
          aria-label="Loading"
          className="h-6 w-6 rounded-full border-2 border-ink-900/20 border-t-ink-900/70 dark:border-ink-100/20 dark:border-t-ink-100/70 animate-spin"
        />
      </div>
    );
  }

  return (
    // `dvh`, not `vh`: on iOS Safari / Chrome Android `vh` resolves against the
    // LARGE viewport, so the last ~55-100px of the shell sit behind the browser
    // toolbar. CallView anchors its control pill to the bottom of this shell, so
    // with `vh` the End / Mute / CC buttons start off-screen on a portrait phone.
    // `overflow-x-clip` (never `hidden`, which would make this a scroll container
    // in both axes and break the sticky top bar) stops any over-wide child from
    // making the whole document pannable sideways.
    <div className="min-h-dvh overflow-x-clip flex flex-col bg-canvas-light dark:bg-canvas-dark text-ink-900 dark:text-ink-100">
      {/* Dark chrome bar (both themes). Yellow is reserved for CTAs, so the
          header is the deep #15131c with a light logo + dark-on-dark controls. */}
      <TopBar right={<>
        {sessionCountdownSeconds != null ? (
          <span className="inline-flex shrink-0 items-center gap-1.5 px-3 py-1 rounded-full text-[0.8rem] font-medium bg-white/[0.06] ring-1 ring-white/10 text-ink-100">
            <ClockIcon className="w-3.5 h-3.5 text-ink-300" />
            <span className="font-mono tabular-nums">
              {/* `hidden` drops the word from the accessibility tree, not just
               *  from the layout, and the clock icon is aria-hidden — so below
               *  md this sr-only copy is the only thing stopping the countdown
               *  being announced as a bare "17:48". Exactly one of the two is
               *  live at any width, so it is never announced twice. */}
              <span className="sr-only md:hidden">Session </span>
              <span className="hidden md:inline">Session </span>
              {String(Math.floor(sessionCountdownSeconds / 60)).padStart(2, '0')}:{String(sessionCountdownSeconds % 60).padStart(2, '0')}
            </span>
          </span>
        ) : null}
        {userId ? (
          <button
            type="button"
            aria-label="Open account"
            onClick={() => setFinancialDrawerOpen(true)}
            className="inline-flex min-w-0 items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.78rem] font-medium bg-white/[0.06] ring-1 ring-white/10 text-ink-100 hover:bg-white/10 transition max-w-[9rem] md:max-w-none"
          >
            <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-status-successBright shrink-0" />
            {/* Stacked below md so a long display name cannot widen the bar.
             *  `min-w-0` has to sit on the BUTTON as well as here: a flex item's
             *  shrink floor is its min-content width, so without it the pill
             *  never shrinks, `max-w-[9rem]` never bites, and the overflow lands
             *  on the last item in the row — the ⋮ menu — instead.
             *  `items-stretch` + `text-left` (a button centres text by default)
             *  make the name resolve its width from the pill rather than from
             *  its own content, which is what keeps the ellipsis inside the
             *  rounded background; with `items-start` it spills out over the
             *  dots. md restores the pre-branch row: gap-1.5 and normal
             *  leading. */}
            <span className="min-w-0 flex flex-col items-stretch text-left leading-tight md:leading-normal md:flex-row md:items-center md:gap-1.5">
              <span className="truncate">{displayName}</span>
              <span className="font-normal text-ink-400 truncate">
                <span className="hidden md:inline">· </span>{role ?? 'guest'}
              </span>
            </span>
          </button>
        ) : (
          <span className="text-[0.85rem] text-ink-400">Not connected</span>
        )}

        {/* Back to app is navigation, not a setting: it stays inline at every
         *  width. Everything else collapses into the overflow menu below md. */}
        {userId && view === 'profile' ? (
          <button
            type="button"
            onClick={() => navigateToView('lobby')}
            className="text-[0.85rem] px-3 py-1.5 rounded-md bg-white/[0.06] ring-1 ring-white/10 text-ink-100 hover:bg-white/10 transition"
          >
            ← Back to app
          </button>
        ) : null}

        <div className="hidden md:flex items-center gap-4">
          {userId && view !== 'profile' ? (
            <button
              type="button"
              aria-label="Profile settings"
              onClick={() => navigateToView('profile')}
              className="p-2 rounded-lg text-ink-300 hover:text-white hover:bg-white/10 transition"
            >
              <GearIcon />
            </button>
          ) : null}

          {userId && adminStatus === 'admin' ? (
            <a
              href="/admin"
              className="text-[0.85rem] px-3 py-1.5 rounded-md bg-white/[0.06] ring-1 ring-white/10 text-ink-100 hover:bg-white/10 transition"
            >
              Admin
            </a>
          ) : null}

          <button
            type="button"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="p-2 rounded-lg text-ink-300 hover:text-white hover:bg-white/10 transition"
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>

          {userId ? (
            <button
              type="button"
              onClick={() => { void signOut(); }}
              className="text-[0.85rem] text-ink-300 hover:text-white px-3 py-1.5 rounded-md hover:bg-white/10 transition"
            >
              Sign out
            </button>
          ) : null}
        </div>

        {/* Signed-out users get no menu. Their only control — the theme toggle —
         *  lives in the `hidden md:flex` cluster above, so below md they have no
         *  theme control at all and the app follows the OS colour scheme; from
         *  md up the inline toggle is there as usual. That is deliberate: a
         *  one-item menu is worse than the control it hides, and a signed-out
         *  phone visitor sees only the landing page. */}
        {userId ? (
          <OverflowMenu
            className="md:hidden shrink-0"
            items={[
              ...(view !== 'profile'
                ? [{ key: 'profile', label: 'Profile', icon: <GearIcon />, onSelect: () => navigateToView('profile') }]
                : []),
              {
                key: 'theme',
                label: theme === 'dark' ? 'Light mode' : 'Dark mode',
                icon: theme === 'dark' ? <SunIcon /> : <MoonIcon />,
                onSelect: toggleTheme,
              },
              ...(adminStatus === 'admin'
                ? [{ key: 'admin', label: 'Admin', onSelect: () => { window.location.href = '/admin'; } }]
                : []),
              { key: 'signout', label: 'Sign out', onSelect: () => { void signOut(); } },
            ]}
          />
        ) : null}
      </>} />

      {isCall ? (
        <LobbyProvider>
          <CallView />
        </LobbyProvider>
      ) : (
        <main
          className={`flex-1 w-full mx-auto px-5 sm:px-8 py-8 sm:py-12 ${isLearnerLobby ? 'max-w-[1200px]' : 'max-w-[840px]'}`}
        >
          {view === 'login' ? <LoginView />
            : view === 'login-only' ? <LoginOnlyView />
            : view === 'register-learner' ? <RegisterLearnerView />
            : view === 'register-guide' ? <RegisterGuideView />
            : view === 'forgot' ? <ForgotPasswordView />
            : view === 'reset' ? <ResetPasswordView />
            : view === 'rate' ? <RatingView />
            : view === 'profile' ? <ProfileView />
            : view === 'support' ? <SupportView onBack={() => setView(lastAppView.current)} />
            : accountPending ? <GuideStageRouter />
            : (
              <LobbyProvider>
                {view === 'lobby'    && <LobbyView />}
                {view === 'incoming' && <IncomingView />}
                {view === 'loopback' && <LoopbackView />}
              </LobbyProvider>
            )}
        </main>
      )}

      {!isCall ? (
        <footer className="border-t border-ink-200/60 dark:border-white/5">
          <div className="w-full max-w-[1280px] mx-auto px-5 sm:px-8 py-6
                          flex flex-col sm:flex-row items-center justify-between gap-3
                          text-[0.75rem] tracking-wide uppercase text-ink-500 dark:text-ink-500">
            <span className="normal-case">© 2025 ASL Go™️ is powered by SignWow USA</span>
            <nav className="flex items-center gap-5">
              <button type="button" onClick={() => openInfo('support')} className="hover:text-ink-700 dark:hover:text-ink-300 transition">
                Support
              </button>
              <a
                href="https://aslgo.com/legal#Privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-ink-700 dark:hover:text-ink-300 transition"
              >
                Privacy Policy
              </a>
              <a
                href="https://aslgo.com/legal#Terms"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-ink-700 dark:hover:text-ink-300 transition"
              >
                Terms of Use
              </a>
            </nav>
          </div>
        </footer>
      ) : null}

      <DebugPanel />
      {role ? (
        <FinancialDrawer open={financialDrawerOpen} role={role} onClose={() => setFinancialDrawerOpen(false)} />
      ) : null}
      {idle.prompting && (
        <IdleModal remainingMs={idle.remainingMs} onDismiss={idle.dismiss} />
      )}
    </div>
  );
}
